/**
 * Where a project is in its life, as the Projects UI shows it: the four-stage track, the list
 * tabs, deadline urgency, and "updated 2h ago". Pure, so both pages share one set of rules.
 */
import type { ProjectStatus } from "@/types/license-project";
import type { StatusPillTone } from "@/components/ui/status-pill";
import {
  PROJECT_STAGE_LABEL,
  PROJECT_STATUS_CONFIG,
  PROJECT_STATUS_PROGRESS,
  projectListStatusLabel,
} from "@/components/licensing/constants";

/** The four stops of the stage track, in the one vocabulary (decision 10). */
export const PROJECT_STAGES = [
  PROJECT_STAGE_LABEL.active,
  PROJECT_STAGE_LABEL.submitted,
  PROJECT_STAGE_LABEL.negotiating,
  PROJECT_STAGE_LABEL.completed,
] as const;

/** Index into PROJECT_STAGES, or -1 for an archived project (off the track). */
export function projectStageIndex(status: ProjectStatus): number {
  switch (status) {
    case "draft":
    case "active":
      return 0;
    case "submitted":
      return 1;
    case "negotiating":
      return 2;
    case "completed":
      return 3;
    default:
      return -1;
  }
}

export interface ProjectTab {
  key: "all" | "shortlisting" | "submitted" | "negotiating" | "cleared" | "archived";
  label: string;
  /** Statuses the tab filters to; null = every status. */
  statuses: ProjectStatus[] | null;
}

export const PROJECT_TABS: ProjectTab[] = [
  { key: "all", label: "All", statuses: null },
  { key: "shortlisting", label: PROJECT_STAGE_LABEL.active, statuses: ["draft", "active"] },
  { key: "submitted", label: PROJECT_STAGE_LABEL.submitted, statuses: ["submitted"] },
  { key: "negotiating", label: PROJECT_STAGE_LABEL.negotiating, statuses: ["negotiating"] },
  { key: "cleared", label: PROJECT_STAGE_LABEL.completed, statuses: ["completed"] },
  // Decision 13: archived projects have their own tab; "All" (no status filter) leaves them out.
  { key: "archived", label: PROJECT_STAGE_LABEL.archived, statuses: ["archived"] },
];

/**
 * What a project card states about its status: the chip's words and tone, and where the stage
 * track sits (null = no track). The words are the one stage vocabulary (projectListStatusLabel);
 * the position is PROJECT_STATUS_PROGRESS, shared with the project header so
 * the list and the page cannot place one project differently; `archived` draws no track. An unknown
 * status gets humanised words, the neutral tone and no track, never a crash on a missing entry.
 */
export function projectCardStatus(status: string | null | undefined): {
  label: string;
  tone: StatusPillTone;
  progress: number | null;
} {
  const key = String(status ?? "");
  const known = Object.prototype.hasOwnProperty.call(PROJECT_STATUS_CONFIG, key) ? (key as ProjectStatus) : null;
  return {
    label: projectListStatusLabel(key),
    tone: known ? PROJECT_STATUS_CONFIG[known].tone : "neutral",
    progress: known ? PROJECT_STATUS_PROGRESS[known] : null,
  };
}

/** The tab a status filter selects, in the words the tabs use ("shortlisting", "negotiating"…). */
export function describeStatusFilter(filter: ProjectStatus[] | null): string | null {
  const key = tabForFilter(filter);
  if (key === "all") return null;
  return PROJECT_TABS.find((t) => t.key === key)?.label.toLowerCase() ?? null;
}

/**
 * The empty default list's title when every project is archived (the default list leaves them out,
 * decision 13): never "No projects yet" to a team that has some (review Q8-14). Home and /projects.
 */
export function allArchivedTitle(archived: number): string {
  return archived === 1 ? "Your only project is archived" : `All ${archived} of your projects are archived`;
}

/**
 * What the list says when it has nothing to show, and whether it offers "Create your first project".
 * Every active narrowing is named, so an empty filtered list never claims the buyer has no projects.
 * The create prompt is offered only when nothing narrows the list (and the viewer may act), because
 * only then is "no projects yet" what the data shows, unless archived projects exist
 * (`archivedCount`, from the stats): then it says they are archived (`allArchived`).
 */
export function projectsEmptyState({
  searchQuery,
  statusFilter,
  campaignFilter,
  canAct,
  archivedCount = 0,
}: {
  searchQuery: string;
  statusFilter: ProjectStatus[] | null;
  campaignFilter: string | null;
  canAct: boolean;
  archivedCount?: number;
}): { title: string; offerCreate: boolean; allArchived?: true } {
  if (searchQuery.trim()) return { title: "No projects match your search", offerCreate: false };
  const stage = describeStatusFilter(statusFilter);
  if (campaignFilter) {
    return { title: stage ? `No projects in this campaign are ${stage}` : "No projects in this campaign", offerCreate: false };
  }
  if (stage) return { title: `No projects are ${stage}`, offerCreate: false };
  if (archivedCount > 0) return { title: allArchivedTitle(archivedCount), offerCreate: canAct, allArchived: true };
  return { title: "No projects yet", offerCreate: canAct };
}

/** What the header reads from `GET /api/license-projects/stats` (project_service.fetch_project_stats). */
export interface ProjectsHeaderStats {
  total: number;
  /** Projects per stored status. A status no project holds is absent, i.e. 0. */
  by_status?: Partial<Record<string, number>>;
}

/**
 * The header's numbers after "{n} projects", or null when there is nothing true to add.
 *
 * The stats cover every project, while "{n} projects" is the list's own total, which the stage,
 * campaign and search filters narrow. So the numbers are shown only while nothing narrows the list,
 * when both count the same projects; under a filter "2 projects · 5 open" read as five of
 * two. Without `by_status` there is nothing to count from, so nothing is added, never guessed.
 */
export function projectsHeaderSummary(
  stats: ProjectsHeaderStats | null,
  { statusFilter, campaignFilter, searchQuery = "" }: { statusFilter: ProjectStatus[] | null; campaignFilter: string | null; searchQuery?: string }
): string | null {
  if (!stats || stats.total <= 0 || statusFilter?.length || campaignFilter || searchQuery.trim()) return null;
  if (!stats.by_status) return null;
  const b = stats.by_status;
  const inProgress = (b.draft ?? 0) + (b.active ?? 0) + (b.submitted ?? 0) + (b.negotiating ?? 0);
  const licensed = b.completed ?? 0;
  const parts = [`${inProgress} open`];
  // The stage word (decision 10; the tab says "Licensed"), lower-case mid-sentence like "open".
  if (licensed > 0) parts.push(`${licensed} ${PROJECT_STAGE_LABEL.completed.toLowerCase()}`);
  return parts.join(" · ");
}

/** Which tab a store filter corresponds to (the store keeps statuses, not tab keys). */
export function tabForFilter(filter: ProjectStatus[] | null): ProjectTab["key"] {
  if (!filter || filter.length === 0) return "all";
  const key = [...filter].sort().join(",");
  const tab = PROJECT_TABS.find((t) => t.statuses && [...t.statuses].sort().join(",") === key);
  return tab?.key ?? "all";
}

/** A tab's count from the stats endpoint's per-status counts. */
export function tabCount(tab: ProjectTab, byStatus: Partial<Record<string, number>> | undefined, total: number): number {
  // The stats count every project; "All" lists every one but the archived (decision 13).
  if (!tab.statuses) return Math.max(0, total - (byStatus?.archived ?? 0));
  return tab.statuses.reduce((sum, s) => sum + (byStatus?.[s] ?? 0), 0);
}

export type DeadlineTone = "overdue" | "soon" | "normal";

export interface DeadlineState {
  daysLeft: number;
  tone: DeadlineTone;
  label: string;
}

const DAY_MS = 86_400_000;
const SOON_DAYS = 7;

/**
 * Deadline urgency on calendar days. A deadline is a date, not an instant, so both sides compare
 * as UTC midnights — otherwise "today" flips to "overdue" partway through the day west of UTC.
 */
export function deadlineState(deadline: string | null | undefined, now: Date = new Date()): DeadlineState | null {
  if (!deadline) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(deadline);
  if (!match) return null;
  const due = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const daysLeft = Math.round((due - today) / DAY_MS);

  if (daysLeft < 0) {
    const n = -daysLeft;
    return { daysLeft, tone: "overdue", label: `${n} day${n === 1 ? "" : "s"} overdue` };
  }
  if (daysLeft === 0) return { daysLeft, tone: "soon", label: "Due today" };
  if (daysLeft <= SOON_DAYS) {
    return { daysLeft, tone: "soon", label: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` };
  }
  const date = new Date(due).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
    ...(new Date(due).getUTCFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return { daysLeft, tone: "normal", label: `Due ${date}` };
}

/**
 * A project's deadline as the card, the Overview and the header show it: none once nothing is due
 * any more, i.e. the project is Licensed or archived (an archived card read "N days overdue" in red,
 * review Q8-14).
 */
export function projectDeadlineState(
  project: { status: ProjectStatus; deadline: string | null | undefined },
  now: Date = new Date()
): DeadlineState | null {
  if (project.status === "completed" || project.status === "archived") return null;
  return deadlineState(project.deadline, now);
}

/** "just now", "5m ago", "2h ago", "3d ago", then a short date (with the year when it isn't this year). */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(then.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

/** Up to two initials for an avatar chip. */
export function initials(name: string | null | undefined): string {
  const words = (name ?? "").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  return (words[0][0] + (words[1]?.[0] ?? "")).toUpperCase();
}
